import { Button, Card, message, Upload } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { UploadIconComponent } from './Icons';
import { UploadProps } from 'antd/lib';
import TrashIcon from 'src/assets/icons/trashIcone';
import ViewIcon from 'src/assets/icons/viewIcone';
import HeadingTypography from 'src/app/components/shared/Typography/Heading';
import { UploadedFile } from '@rasayi-workspace/shared';
import ServerImage from 'src/app/components/shared/Image/serverImage';
import { DeleteConfirmationModal } from 'src/app/components/shared';
import { deleteDocument } from 'src/app/services/lead';
import { useExistingLead } from 'src/app/hooks/useGetExistingLead';
import ImagePreviewModal from 'src/app/components/shared/modals/ImagePreviewModal';

interface DocumentsUploadProp {
    required?: boolean;
    title?: string;
    maxFiles?: number;
    showUploadButton?: boolean;
    uploadedFiles?: UploadedFile[];
    uploadFiles?: (files: UploadedFile[]) => void;
    isUploadAble?: boolean;
    leadId: string;
}

export const DocumentsUpload: React.FC<DocumentsUploadProp> = ({ title, maxFiles = 1, uploadedFiles = [], uploadFiles, leadId, isUploadAble = true, required = false }) => {
    const isFirstRender = useRef(true);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);

    const { fetchExistingLead } = useExistingLead(leadId, true);
    const [messageApi, contextHolder] = message.useMessage();

    const [files, setFiles] = useState<UploadedFile[]>([]);

    const handleUpload: UploadProps['onChange'] = ({ fileList }) => {
        const newFiles: UploadedFile[] = fileList.map(file => {
            const originFile = file.originFileObj as File;
            return {
                uid: file.uid ?? `${Date.now()}`,
                file: originFile,
                url: URL.createObjectURL(originFile),
            };
        });
        if (newFiles.length + files.length <= maxFiles) {
            const allFiles = [...files, ...newFiles];
            setFiles(allFiles);
            if (uploadFiles && allFiles) uploadFiles(allFiles);
        }
        return false;
    };

    useEffect(() => {
        if (isFirstRender.current && uploadedFiles.length) {
            setFiles(uploadedFiles);
            isFirstRender.current = false;
        }
    }, [uploadedFiles]);
    const handleRemoveDocument = async (id: string) => {
        try {
            await deleteDocument(leadId, id);
            fetchExistingLead();
            return true;
        } catch (error) {
            messageApi.error('Failed to delete document');
            return false;
        }
    };

    const handleRemove = (uid: string) => {
        const file = files.find(file => file.uid === uid);
        if (file) {
            DeleteConfirmationModal({
                content: `Are you sure you want to delete this image?`,
                onOk: async () => {
                    let isDeleted = true;
                    if (!file.file) {
                        isDeleted = await handleRemoveDocument(uid);
                    }
                    if (!isDeleted) return;
                    const allFiles = files.filter(file => file.uid !== uid);
                    setFiles(allFiles);
                    if (uploadFiles) uploadFiles(allFiles);
                },
            });
        }
    };

    const EmptyCard = () => <Card style={{ width: 84, height: 84, border: '1px dashed #DBD7D7' }} />;

    // Upload File Card
    const UploadCard = () => (
        <Upload showUploadList={false} onChange={handleUpload} beforeUpload={() => false}>
            <Button
                type="dashed"
                style={{
                    width: 84,
                    height: 84,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #d9d9d9',
                }}
                icon={<UploadIconComponent />}
            >
                <span style={{ fontSize: 12, marginTop: 0 }}>Upload</span>
            </Button>
        </Upload>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {contextHolder}

            <HeadingTypography classNames="font-medium text-start pb-[16px]" fontSize={14}>
                {title} {required && <span className="text-[#BD273B]"> *</span>}
            </HeadingTypography>

            <div
                style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    maxWidth: '100%',
                }}
            >
                {Array.from({ length: maxFiles }).map((_, index) => {
                    if (index < files.length) {
                        return (
                            <Card
                                key={index}
                                className="flex justify-center items-center"
                                style={{
                                    width: 84,
                                    height: 84,
                                    textAlign: 'left',
                                    backgroundColor: '#FAFAFA',
                                    border: '1px dashed #DBD7D7',
                                }}
                            >
                                <div className="relative w-[84px]  h-[84px]">
                                    <div className="  absolute inset-0 flex items-center justify-center bg-black opacity-50 w-[84px] h-[84px]">
                                        <div className="flex gap-2 cursor-pointer">
                                            <div
                                                onClick={() => {
                                                    setPreviewVisible(true);
                                                    setPreviewFile(files[index]);
                                                }}
                                            >
                                                <ViewIcon />
                                            </div>
                                            <div onClick={() => handleRemove(files[index].uid)}>
                                                <TrashIcon />
                                            </div>
                                        </div>
                                    </div>
                                    <ServerImage fileKey={files[index].url || ''} alt={files[index]?.file?.name} width={84} height={84} />
                                    {/* <img src={files[index].url} alt={files[index]?.file?.name} width={84} height={84} className=" w-[84px]  h-[84px]" style={{ objectFit: 'cover' }} /> */}
                                </div>
                            </Card>
                        );
                    } else if (index === maxFiles - 1) {
                        return <>{isUploadAble ? <UploadCard key={index} /> : <EmptyCard key={index} />}</>;
                    } else {
                        return <EmptyCard key={index} />;
                    }
                })}
            </div>
            {previewVisible && <ImagePreviewModal visible={previewVisible} file={previewFile} setVisible={setPreviewVisible} />}
        </div>
    );
};
